import type { SEOMeta } from "@/types"
import { useEffect } from "react"

const DEFAULT_TITLE = "Orchestrate — Payments that scale with you"
const DEFAULT_DESCRIPTION =
  "Orchestrate empowers modern businesses to scale, innovate, and manage complex payment flows through a single, unified API."

export function useSEO(meta: Partial<SEOMeta>): void {
  useEffect(() => {
    const title = meta.title ? `${meta.title} | Orchestrate` : DEFAULT_TITLE
    const description = meta.description ?? DEFAULT_DESCRIPTION

    // Title
    document.title = title

    // Meta description
    setMeta("name", "description", description)

    // Keywords
    if (meta.keywords) {
      setMeta("name", "keywords", meta.keywords)
    }

    // OG tags
    setMeta("property", "og:title", title)
    setMeta("property", "og:description", description)

    if (meta.ogImage) {
      setMeta("property", "og:image", meta.ogImage)
    }

    // Canonical
    if (meta.canonical) {
      let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
      if (!canonical) {
        canonical = document.createElement("link")
        canonical.rel = "canonical"
        document.head.appendChild(canonical)
      }
      canonical.href = meta.canonical
    }

    return () => {
      document.title = DEFAULT_TITLE
    }
  }, [meta.title, meta.description, meta.keywords, meta.ogImage, meta.canonical])
}

function setMeta(attr: "name" | "property", key: string, value: string): void {
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement("meta")
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.content = value
}
